/**
 * "Is this line the recipe's yield rather than one of its ingredients?"
 *
 * French recipe sites (Ricardo and others) routinely put the serving count in
 * the ingredient list itself — `recipeIngredient: ["Pour 4 personnes", "8
 * côtelettes d'agneau", ...]`. Nothing downstream could tell the difference, so
 * "Pour 4 personnes" rendered as a checkable ingredient row, was counted in the
 * pantry coverage, and landed on the grocery list (#594).
 *
 * This is the single predicate for that question. It is deliberately narrow.
 * The tempting pattern — /\d+\s*(personnes?|portions?)/ — also matches "4
 * pommes de terre Russett (1 par personne)" and "4 portions de saumon", both
 * real foods someone has to buy, and the first one appears in the very
 * screenshot that reported #594. An ingredient wrongly deleted is discovered at
 * the store; a stray line is merely ugly. So the two branches below are both
 * anchored, and anything not clearly a yield is kept.
 */

/** Non-breaking spaces are everywhere in French typography ("Portions : 4"). */
function tidy(text: string): string {
  return text
    .replace(/[   ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Branch 1 — an explicit yield keyword opens the line, followed by a count.
 * With the keyword present the trailing noun is unconstrained, because
 * "Rendement : 12 muffins" and "Makes 12 cookies" name whatever the recipe
 * produces. No ingredient line opens with one of these words *and* a number.
 */
const LEAD_KEYWORD =
  /^(?:pour|donne|rendement|portions?|serves?|yields?|makes?)\b[\s:]*\d/i;

/**
 * Branch 2 — no keyword, just a count and a serving noun ("4 personnes", the
 * form in the issue title). Anchored at the end: the signal that separates a
 * yield from an ingredient is that *nothing follows* the noun. "4 portions de
 * saumon" continues, so it is food.
 */
const BARE_COUNT =
  /^\d+(?:\s*(?:[-–]|à|a|to)\s*\d+)?\s*(?:personnes?|portions?|servings?|parts?)\s*\.?$/i;

export function isYieldLine(text: string): boolean {
  if (!text) return false;
  const line = tidy(text);
  if (!line) return false;
  return LEAD_KEYWORD.test(line) || BARE_COUNT.test(line);
}
